import {action, computed} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {IChatApi} from '../../services/ts-chat-api/ChatApi';
import {ITsEnvironmentDetect} from '@techsee/techsee-common/lib/helpers/ts-environment-detect';
import {SmsStatusModalState} from './SmsStatusModal.state';

interface SmsStatusModalOptions {
    title?: string;
    message?: string;
    question?: boolean;
    useNewInterface?: boolean;
}

interface CloseResult {
    answer?: boolean | 'abort';
    completionFunc?: () => void;
}

export class SmsStatusModalController extends TechseeBaseController {
    private resolvePromise: ((result?: CloseResult) => void) | null = null;
    private rejectPromise: (() => void) | null = null;
    private chatApiUnsubscribe: (() => void) | null = null;
    private _baseUseNewInterface: boolean;

    constructor(
        public state: SmsStatusModalState,
        private chatApi: IChatApi,
        private environmentService: ITsEnvironmentDetect,
        private displayTabletAsDesktop: boolean
    ) {
        super();
        this._baseUseNewInterface = (this as any).useNewInterface;
    }

    @action
    open(opts: SmsStatusModalOptions): Promise<CloseResult | void> {
        this.state.title = opts.title || '';
        this.state.message = opts.message || '';
        this.state.question = opts.question !== undefined ? opts.question : false;
        this.state.useNewInterfaceOverride = opts.useNewInterface;
        const useNewInterface =
            this.state.useNewInterfaceOverride !== undefined
                ? this.state.useNewInterfaceOverride
                : this._baseUseNewInterface;
        this.state.isMobileValue = useNewInterface && this.environmentService.isMobile(this.displayTabletAsDesktop);

        this.state.isVisible = true;

        const handleClientConnected = () => {
            this.answer(true);
        };
        this.chatApi.on('clientConnected', handleClientConnected);
        this.chatApiUnsubscribe = () => {
            this.chatApi.off('clientConnected', handleClientConnected);
        };

        return new Promise<CloseResult | void>((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
    }

    @action
    close(result?: CloseResult) {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;

        if (this.chatApiUnsubscribe) {
            this.chatApiUnsubscribe();
            this.chatApiUnsubscribe = null;
        }

        if (this.resolvePromise) {
            this.resolvePromise(result);
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        this.emit('CLOSED');
    }

    @action
    dismiss() {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;

        if (this.chatApiUnsubscribe) {
            this.chatApiUnsubscribe();
            this.chatApiUnsubscribe = null;
        }

        if (this.rejectPromise) {
            this.rejectPromise();
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        this.emit('DISMISSED');
    }

    @action
    answer(result: boolean) {
        this.close({answer: result});
    }

    @action
    ok() {
        this.close();
    }

    @computed
    get useNewInterface() {
        return this.state.useNewInterfaceOverride !== undefined
            ? this.state.useNewInterfaceOverride
            : this._baseUseNewInterface;
    }

    @computed
    get isMobile(): boolean {
        return this.state.isMobileValue;
    }
}
